import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  pricingPlansHighlightedPlanAtom,
  pricingPlansModalOpenAtom,
} from '../../billingAtoms';
import { PracticeSeatUpgradePlans } from './practice-seat-upgrade-plans';
import { pricingPlansModalMessages } from './pricing-plans-modal.messages';
import { PricingPlansStatusBanner } from './pricing-plans-status-banner';
import { PricingPlanCards } from './PricingPlanCards';
import { usePricingPlansModalState } from './usePricingPlansModalState';

import { TypographyH2 } from '@heidi/design-system/typography';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { type BillingSeatType, type SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo } from '@heidi/workspace/data';
import { useAtom, useSetAtom } from 'jotai';
import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { getCommerceBillingUiPorts } from '../../billing-ui-ports';

/** Who is looking, written by the body so the shell's events carry it. */
type PricingPlansAnalyticsContext = {
  currentPlan?: SubscriptionTier;
  currentSeatType?: BillingSeatType;
  isAdmin?: boolean;
};

export const PricingPlansModal = () => {
  const [isOpen, setIsOpen] = useAtom(pricingPlansModalOpenAtom);
  const setHighlightedPlan = useSetAtom(pricingPlansHighlightedPlanAtom);
  const wasOpenRef = useRef(false);
  const analyticsContextRef = useRef<PricingPlansAnalyticsContext>({});

  // Radix and vaul call this only for a user dismissal; a CTA that closes the
  // modal writes the atom directly, so it is never counted as a close.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        const { currentPlan, currentSeatType } = analyticsContextRef.current;
        getCommerceBillingUiPorts().track({
          options: { amplitude: false },
          event_name: getCommerceBillingUiPorts().trackingEventName.ButtonClicked,
          props: {
            screen_name: ScreenName.PricingPlansModal,
            action: ButtonAction.PricingPlansModalClose,
            label: 'close',
            ...(currentPlan && { current_plan: currentPlan }),
            ...(currentSeatType && { current_seat_type: currentSeatType }),
          },
        });
      }
      setIsOpen(open);
    },
    [setIsOpen]
  );

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) setHighlightedPlan(null);
    wasOpenRef.current = isOpen;
  }, [isOpen, setHighlightedPlan]);

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleOpenChange}>
      {/*
        880 wide with 48/32 padding is the design frame. The height stays
        content-driven — pinning it to the design's 703 would depend on every
        design-system control matching its Figma instance to the pixel, and the
        failure mode is clipped content. `PricingPlanCards` freezes the card row
        at its measured height instead, so the dialog never grows on expand.
        A single-card body (Practice seat checkout, Scribe Team upgrade) narrows
        it to the 540 design frame through `:has`, since the plan state is only
        read once the content mounts.
      */}
      <DialogDrawerContent className="flex max-h-[90dvh] min-h-0 w-full flex-col overflow-hidden bg-surface-elevated px-8 pb-8 pt-12 sm:max-w-[880px] sm:has-[[data-single-plan]]:max-w-[540px]">
        <PricingPlansModalBody analyticsContextRef={analyticsContextRef} />
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

function PricingPlansModalBody({
  analyticsContextRef,
}: {
  analyticsContextRef: MutableRefObject<PricingPlansAnalyticsContext>;
}) {
  const modalState = usePricingPlansModalState();
  const {
    isPracticeSeatCheckoutOffered,
    isScribeTeamUpgradeOffered,
    currentPlan,
    currentSeatType,
  } = modalState;
  const { hasTeam, isTeamAdmin, isLoadingTeamDetails, isTeamDataReady } =
    useCombinedQueriesTeamGeneralInfo();
  const isAdmin = hasTeam ? isTeamAdmin : undefined;
  useEffect(() => {
    analyticsContextRef.current = {
      ...(currentPlan && { currentPlan }),
      ...(currentSeatType && { currentSeatType }),
      ...(isAdmin !== undefined && { isAdmin }),
    };
  }, [analyticsContextRef, currentPlan, currentSeatType, isAdmin]);

  // The body mounts once per open. The view waits for the plan and a settled
  // team role so it is never counted without them.
  const isTeamRoleSettled = !isLoadingTeamDetails && isTeamDataReady !== false;
  const hasTrackedViewRef = useRef(false);
  useEffect(() => {
    if (hasTrackedViewRef.current || !currentPlan || !isTeamRoleSettled) return;
    hasTrackedViewRef.current = true;
    getCommerceBillingUiPorts().track({
      options: { amplitude: false },
      event_name: getCommerceBillingUiPorts().trackingEventName.ModalViewed,
      props: {
        screen_name: ScreenName.PricingPlansModal,
        current_plan: currentPlan,
        ...(isAdmin !== undefined && { is_admin: isAdmin }),
      },
    });
  }, [currentPlan, isTeamRoleSettled, isAdmin]);

  // Both team upgrades share the one Practice card into the seat checkout:
  // Practice Standard picks Max seats for members, Scribe Team moves the team.
  const seatCheckoutFromPlan = isPracticeSeatCheckoutOffered
    ? 'practice'
    : isScribeTeamUpgradeOffered
      ? 'scribeTeam'
      : null;

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto overflow-x-hidden pb-10 sm:pb-0"
      data-single-plan={seatCheckoutFromPlan !== null || undefined}
    >
      <TypographyH2 className="text-center text-text-primary">
        <FormattedMessage
          {...(seatCheckoutFromPlan !== null
            ? pricingPlansModalMessages.teamUpgradeTitle
            : pricingPlansModalMessages.title)}
        />
      </TypographyH2>

      {seatCheckoutFromPlan !== null ? (
        <div className="flex flex-col gap-4">
          <PricingPlansStatusBanner />
          <PracticeSeatUpgradePlans
            currentPlan={currentPlan}
            currentSeatType={currentSeatType}
            fromPlan={seatCheckoutFromPlan}
          />
        </div>
      ) : (
        <>
          <PricingPlansStatusBanner />
          <PricingPlanCards modalState={modalState} />
        </>
      )}
    </div>
  );
}
